// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// tuning.ts — every load-bearing constant, in one place, so the balance sim can move one at a time.
//
// The two numbers the whole design rests on are pinned by tests rather than by comment:
//   WINDOW_SECS 2.5      — set it to 0.5 and bus trips collapse toward zero (balance.test.ts)
//   SUPPRESS_BY_WATER -20 — set it to -6 and the water LOSES: the tutorial's step-6 watch window
//                           goes from 5.5 seconds to a compartment burning down, i.e. the play the
//                           whole tutorial exists to teach becomes impossible (tutorial.test.ts,
//                           which reds four cases on that one edit and carries the -6 arm as a
//                           standing negative control).
// Both assertions exist so a tuning pass turns something RED instead of quietly changing the game.
//
// withTuning() is how the sim explores; nothing in src/ ever mutates T outside it.

export interface Tuning {
  TICK_HZ: number;
  SNAP_HZ: number;
  CATCHUP_STEPS: number;
  INPUT_DELAY_TICKS: number;

  // power / conduction
  COST_WARP: number;
  COST_DESTROYED: number;
  CELL_CAPACITY: number;
  CELL_DRAIN: number;
  CELL_RECHARGE: number;
  CELL_DROWN_FLOOD: number;

  // heat
  HEAT_PER_SEGMENT: number;
  COOL_PASSIVE: number;
  COOL_COOLANT: number;
  SCRAM_AT: number;
  SCRAM_OUTPUT: number;
  SCRAM_SECS: number;
  SCRAM_HULL: number;
  SCRAM_DOUBLE_WINDOW: number;

  // the LOAD WINDOW — charged on action START, not on commit
  WINDOW_SECS: number;
  HEAT_REDRAW_BASE: number;
  HEAT_REDRAW_PER_CELL: number;
  HEAT_SEAL: number;
  HEAT_DOUSE: number;
  HEAT_VENT: number;
  HEAT_FORCE_START: number;
  /** Extra charge per step up the load window: cost = base x (1 + (ordinal-1) x slope). */
  ORDINAL_SLOPE: number;
  TRIP_ORDINAL: number;
  TRIP_BLACKOUT_TICKS: number;
  TRIP_HEAT: number;
  TRIP_HULL: number;

  // fire
  FIRE_GROW: number;
  FIRE_GROW_POWERED: number;
  FIRE_SPREAD: number;
  SUPPRESS_BY_WATER: number;
  SUPPRESS_FLOOD_AT: number;
  FIRE_IGNITE_HEAT: number;
  FIRE_IGNITE_DIV: number;
  DESTROY_SECS: number;
  DESTROY_HULL: number;

  // flood
  FLOOD_INFLOW: number;
  FLOOD_SEVERITY: number;
  FLOOD_SPREAD: number;
  FLOOD_HULL: number;
  SHORT_P: number;
  SHORT_FLOOD_AT: number;
  SHORT_FIRE: number;
  SHORT_HULL: number;
  SHORT_HEAT: number;
  SHORT_CUT_SECS: number;

  // verbs
  SEAL_HOLD: number;
  DOUSE_HOLD: number;
  DOUSE_RATE: number;
  VENT_HOLD: number;
  VENT_HULL: number;
  FORCE_OUTPUT: number;
  FORCE_HEAT: number;

  // systems
  PUMP_RATE: number;
  SUPPRESSION_RATE: number;
  SENSORS_HORIZON: number;
  BASE_HORIZON: number;

  // termination
  DRIFT_GRACE: number;
  DRIFT_HULL_PER_BLOCK: number;
  DRIFT_HULL_CAP: number;
}

export const BASE: Tuning = {
  TICK_HZ: 30,
  SNAP_HZ: 12,
  CATCHUP_STEPS: 30,
  INPUT_DELAY_TICKS: 4,

  COST_WARP: 2,
  COST_DESTROYED: 3,
  CELL_CAPACITY: 12.0,
  CELL_DRAIN: 1.0,
  CELL_RECHARGE: 0.25,
  CELL_DROWN_FLOOD: 40,

  HEAT_PER_SEGMENT: 0.7,
  COOL_PASSIVE: 1.1,
  COOL_COOLANT: 2.6,
  SCRAM_AT: 100,
  SCRAM_OUTPUT: 2,
  SCRAM_SECS: 8.0,
  SCRAM_HULL: 5,
  SCRAM_DOUBLE_WINDOW: 20.0,

  WINDOW_SECS: 2.5,
  // THE BASE IS SMALL AND THE ORDINAL MULTIPLIER IS THE MECHANIC. At a base of 4.0 the reactor was
  // cooked by the sheer VOLUME of honest repairs — sixty to eighty actions a run is 240-500 heat, two
  // to five SCRAMs — so a bigger crew was punished simply for doing more work, and SCRAM became the
  // top cause of death at party four. Halving the base leaves the thing that actually matters intact:
  // the second action inside 2.5 seconds still costs double, the third triple, and the fourth still
  // drops the whole ship dark. The cost of BUNCHING is the game; the cost of working is not.
  HEAT_REDRAW_BASE: 1.5,
  HEAT_REDRAW_PER_CELL: 1.0,
  HEAT_SEAL: 2.5,
  HEAT_DOUSE: 2.5,
  HEAT_VENT: 2.5,
  HEAT_FORCE_START: 5.0,
  ORDINAL_SLOPE: 0.6,
  TRIP_ORDINAL: 4,
  TRIP_BLACKOUT_TICKS: 8,
  TRIP_HEAT: 10,
  TRIP_HULL: 3,

  FIRE_GROW: 6.0,
  FIRE_GROW_POWERED: 12.0,
  FIRE_SPREAD: 3.6,
  SUPPRESS_BY_WATER: -20.0,
  SUPPRESS_FLOOD_AT: 30,
  // A hot core sets its own live compartments alight. Measured at 70 with a /10 rate this was not a
  // pressure, it was a wipe: the bot reached heat 70 inside 20s on every seed and the whole ship
  // caught at once, so 100% of runs died to the crew venting compartments to keep up. At 80 with a
  // /25 rate it is what it should be — a reason not to sit at the top of the gauge.
  FIRE_IGNITE_HEAT: 80,
  FIRE_IGNITE_DIV: 25,
  DESTROY_SECS: 4.0,
  DESTROY_HULL: 11,

  FLOOD_INFLOW: 7.0,
  // Compounds every 15s. At 1.05 the water never outran a working pump and Scuttle simply did not
  // end — 100% of runs hit the 420s cap with the crew comfortable. At 1.07 a breach late in a long
  // run is a genuinely different event from the same breach at the start, which is what a survival
  // mode needs and what the hazard-ramp assertion measures.
  FLOOD_SEVERITY: 1.06,
  FLOOD_SPREAD: 2.5,
  // Water was killing 100% of runs in two of the three modes at 2.0. A single cause taking every
  // loss means the other four systems are decoration, which is what the loss-cause spread assertion
  // in balance.test.ts exists to catch.
  FLOOD_HULL: 0.9,
  SHORT_P: 0.06,
  SHORT_FLOOD_AT: 40,
  SHORT_FIRE: 20,
  SHORT_HULL: 6,
  SHORT_HEAT: 4,
  SHORT_CUT_SECS: 1.5,

  SEAL_HOLD: 0.8,
  DOUSE_HOLD: 1.0,
  DOUSE_RATE: 55,
  VENT_HOLD: 0.5,
  VENT_HULL: 6,
  FORCE_OUTPUT: 2,
  FORCE_HEAT: 3.0,

  PUMP_RATE: 9.0,
  SUPPRESSION_RATE: 9.0,
  SENSORS_HORIZON: 8.0,
  BASE_HORIZON: 3.0,

  // The drift clock exists so a ship that can never reach HELM still DIES rather than hanging — it is
  // a softlock backstop, not a hazard. At 0.35/block capped at 2.0 it had become the single largest
  // killer in Patrol, executing crews that were losing slowly for the crime of losing slowly. Halved,
  // it still guarantees termination (a fully stalled ship dies in about three minutes) without being
  // what the run is about.
  // Twenty seconds, not ten. `driftSecs` resets on ANY transit advance, so at ten a crew that simply
  // cycles the cable between halves of the ship — which is what two of the three modes are ABOUT —
  // was taking hull damage continuously, and drift became the top cause of death in both. At twenty
  // it only fires on a ship that genuinely cannot reach HELM at all, which is the softlock it exists
  // to kill.
  DRIFT_GRACE: 20.0,
  // HALVED AGAIN, because the balance sim caught it being the thing the game is about rather than
  // the backstop it is supposed to be: drift was the leading cause of death in Patrol (70% of losses)
  // and Hauler (95%), and Hauler's mean transit AT DEATH was 98% — nearly every seed died within
  // sight of port, to a clock rather than to the ship. A softlock backstop only has to guarantee
  // termination; at 0.09/block capped at 0.5/s a genuinely stalled ship still dies in about four
  // minutes, and a crew that is merely cycling its cable is no longer executed for it.
  DRIFT_HULL_PER_BLOCK: 0.09,
  DRIFT_HULL_CAP: 0.5,
};

export let T: Tuning = BASE;

/** Run `fn` with some constants overridden, then restore. The sim's only way to explore. */
export function withTuning<R>(over: Partial<Tuning>, fn: () => R): R {
  const prev = T;
  T = { ...prev, ...over };
  try {
    return fn();
  } finally {
    T = prev;
  }
}
